import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useLang } from "@/contexts/LanguageContext";
import { tr } from "@/data/translations";
import { getPostBySlug } from "@/data/posts";
import { getAreaBySlug } from "@/data/serviceAreas";

const setMeta = (name, content, attr = "name") => {
  if (!content) return;
  let el = document.head.querySelector(`meta[${attr}="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
};

const setLink = (rel, href, hreflang) => {
  if (!href) return;
  const selector = hreflang
    ? `link[rel="${rel}"][hreflang="${hreflang}"]`
    : `link[rel="${rel}"]:not([hreflang])`;
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    if (hreflang) el.setAttribute("hreflang", hreflang);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
};

const ogSlugFor = (pathname) => {
  const p = pathname.replace(/\/+$/, "") || "/";
  if (p === "/") return "home";
  if (p.startsWith("/services/")) return p.slice("/services/".length);
  if (p.startsWith("/blog/")) return "blog";
  if (p.startsWith("/service-areas/")) return p.slice("/service-areas/".length);
  return p.slice(1);
};

const KNOWN_OG_SLUGS = new Set([
  "home",
  "about",
  "services",
  "washer",
  "dryers",
  "refrigerator",
  "dishwasher",
  "range-oven",
  "contact",
  "blog",
  "hawaii-county",
  "kailua-kona",
  "waimea",
  "captain-cook",
]);

const useSEO = () => {
  const { pathname } = useLocation();
  const { lang } = useLang();

  useEffect(() => {
    const routeKey = pathname.replace(/\/+$/, "") || "/";
    const origin =
      typeof window !== "undefined" && window.location
        ? window.location.origin
        : "";
    const baseUrl = `${origin}${routeKey === "/" ? "/" : routeKey}`;

    let seo;
    let ogImageOverride = null;

    // Blog post detail page — read SEO from posts.js, use post.image as og
    if (routeKey.startsWith("/blog/")) {
      const slug = routeKey.slice("/blog/".length);
      const post = getPostBySlug(slug);
      if (post) {
        seo = post.seo[lang];
        ogImageOverride = post.image;
      }
    }
    // Service area page — read SEO from serviceAreas.js
    if (!seo && routeKey.startsWith("/service-areas/")) {
      const slug = routeKey.slice("/service-areas/".length);
      const area = getAreaBySlug(slug);
      if (area) seo = area.seo[lang];
    }
    if (!seo) {
      seo = tr(lang, `seo.${routeKey}`) || tr(lang, "seo./");
    }

    const ogSlugRaw = ogSlugFor(pathname);
    const ogSlug = KNOWN_OG_SLUGS.has(ogSlugRaw) ? ogSlugRaw : "home";
    const ogImage = ogImageOverride || `${origin}/og/${lang}/${ogSlug}.png`;

    if (seo && typeof seo === "object") {
      document.title = seo.title;
      setMeta("description", seo.description);
      setMeta("og:title", seo.title, "property");
      setMeta("og:description", seo.description, "property");
      setMeta(
        "og:type",
        routeKey.startsWith("/blog/") ? "article" : "website",
        "property",
      );
      setMeta("og:url", baseUrl, "property");
      setMeta("og:locale", lang === "es" ? "es_US" : "en_US", "property");
      setMeta("og:site_name", "Genesis Appliance Repair", "property");
      setMeta("og:image", ogImage, "property");
      if (!ogImageOverride) {
        setMeta("og:image:width", "1200", "property");
        setMeta("og:image:height", "630", "property");
      }
      setMeta("og:image:alt", seo.title, "property");
      setMeta("twitter:card", "summary_large_image");
      setMeta("twitter:title", seo.title);
      setMeta("twitter:description", seo.description);
      setMeta("twitter:image", ogImage);
    }

    setLink("canonical", baseUrl);
    const sep = baseUrl.includes("?") ? "&" : "?";
    setLink("alternate", `${baseUrl}${sep}lang=en`, "en");
    setLink("alternate", `${baseUrl}${sep}lang=es`, "es");
    setLink("alternate", baseUrl, "x-default");
  }, [pathname, lang]);
};

export default useSEO;
